// Libs
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';
// Others
import { RootState } from '~/utils/redux/store';
import {
  changeStatusEstimate,
  convertToJob,
  createEstimate,
  deleteEstimate,
  getDetailEstimate,
  getEstimateDetailUpdate,
  getEstimateScheduleList,
  getListEstimate,
  sendEstimate,
  updateEstimate,
} from '~/thunks/estimate/estimateThunk';
import { IDetailEstimate } from '~/utils/interface/estimate';
import { ISchedule, IScheduleTimeLine } from '~/utils/interface/schedule';
import { CalendarViewMode, ScheduleMode } from '~/utils/type/common';
import { DateFormatEnum } from '~/utils/enum/common';
import { DEFAULT_VIEW_MODE, SCHEDULE_MODE_FULL } from '~/utils/constants/common';

export interface IEstimateState {
  isRefreshEstimateList: boolean;
  estimateDetail: IDetailEstimate;
  scheduleCurrentTime: string;
  isRefreshEstimateSchedule: boolean;
  scheduleEstimate: IScheduleTimeLine[];
  calendarViewMode: CalendarViewMode;
  scheduleMode: ScheduleMode;
  events: ISchedule[];
}

export const initialState: IEstimateState = {
  isRefreshEstimateList: false,
  estimateDetail: {} as IDetailEstimate,
  scheduleCurrentTime: moment().format(DateFormatEnum.YYYY_MM_DD),
  isRefreshEstimateSchedule: false,
  scheduleEstimate: [],
  calendarViewMode: DEFAULT_VIEW_MODE,
  scheduleMode: SCHEDULE_MODE_FULL,
  events: [],
};

const estimateSlice = createSlice({
  name: 'estimate',
  initialState,
  reducers: {
    setRefreshEstimateList: (state, action) => {
      state.isRefreshEstimateList = action.payload;
    },
    setScheduleCurrentTime(state, action: PayloadAction<string>) {
      state.scheduleCurrentTime = action.payload;
    },
    setIsRefreshScheduleData(state, action: PayloadAction<boolean>) {
      state.isRefreshEstimateSchedule = action.payload;
    },
    changeEstimateSchedule(state, action: PayloadAction<IScheduleTimeLine[]>) {
      state.scheduleEstimate = action.payload;
    },
    changeScheduleMode(state, action: PayloadAction<ScheduleMode>) {
      state.scheduleMode = action.payload;
    },
    changeCalendarViewMode(state, action: PayloadAction<CalendarViewMode>) {
      state.scheduleCurrentTime = moment().format(DateFormatEnum.YYYY_MM_DD);
      state.calendarViewMode = action.payload;
    },
  },
  extraReducers: (builder) => {
    // POST: Create Estimate
    builder
      .addCase(createEstimate.pending, (state) => {})
      .addCase(createEstimate.fulfilled, (state) => {})
      .addCase(createEstimate.rejected, (state) => {});

    // GET: Get List Estimate
    builder
      .addCase(getListEstimate.pending, (state) => {})
      .addCase(getListEstimate.fulfilled, (state) => {
        state.isRefreshEstimateList = false;
      })
      .addCase(getListEstimate.rejected, (state) => {});

    // PUT: Update Estimate
    builder
      .addCase(updateEstimate.pending, (_state) => {})
      .addCase(updateEstimate.fulfilled, (_state) => {})
      .addCase(updateEstimate.rejected, (_state) => {});

    // GET: Get Estimate Detail
    builder
      .addCase(getDetailEstimate.pending, (state) => {})
      .addCase(getDetailEstimate.fulfilled, (state, action) => {
        state.estimateDetail = {
          ...state.estimateDetail,
          ...action.payload.data,
        };
      })
      .addCase(getDetailEstimate.rejected, (state) => {});

    // GET: Get estimate schedule list
    builder
      .addCase(getEstimateScheduleList.pending, (state) => {})
      .addCase(getEstimateScheduleList.fulfilled, (state, action) => {
        if (state.calendarViewMode === 'month') {
          state.events = action.payload.data as ISchedule[];
        } else if ('responses' in action.payload.data) {
          state.scheduleEstimate = action.payload.data.responses as IScheduleTimeLine[];
        }
        state.isRefreshEstimateSchedule = false;
      })
      .addCase(getEstimateScheduleList.rejected, (state) => {});

    // GET: Get Estimate Detail Update
    builder
      .addCase(getEstimateDetailUpdate.pending, (_state) => {})
      .addCase(getEstimateDetailUpdate.fulfilled, (_state) => {})
      .addCase(getEstimateDetailUpdate.rejected, (_state) => {});

    // POST: Convert To Job
    builder
      .addCase(convertToJob.pending, (_state) => {})
      .addCase(convertToJob.fulfilled, (_state) => {})
      .addCase(convertToJob.rejected, (_state) => {});

    // DELETE: Delete Estimate
    builder
      .addCase(deleteEstimate.pending, (state) => {})
      .addCase(deleteEstimate.fulfilled, (state) => {})
      .addCase(deleteEstimate.rejected, (state) => {});

    // POST: Send Estimate
    builder
      .addCase(sendEstimate.pending, (_state) => {})
      .addCase(sendEstimate.fulfilled, (_state) => {})
      .addCase(sendEstimate.rejected, (_state) => {});

    // PUT: Change Status Estimate
    builder
      .addCase(changeStatusEstimate.pending, (_state) => {})
      .addCase(changeStatusEstimate.fulfilled, (state) => {
        state.isRefreshEstimateList = true;
      })
      .addCase(changeStatusEstimate.rejected, (_state) => {});
  },
});

// Action
export const estimateActions = estimateSlice.actions;

// Selectors
export const selectIsRefreshEstimateList = (state: RootState) => state.estimate.isRefreshEstimateList;
export const selectEstimateDetail = (state: RootState) => state.estimate.estimateDetail;

// Reducer
const estimateReducer = estimateSlice.reducer;
export default estimateReducer;
